import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { employeeApi } from '../../api/hr';
import { usersApi } from '../../api/admin/users';
import { departmentsApi } from '../../api/admin/departments';
import { positionsApi } from '../../api/admin/positions';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';

const createSchema = z.object({
  userId: z.coerce.number().int().positive({ message: 'Select a user.' }),
  departmentId: z.coerce.number().int().positive().optional(),
  positionId: z.coerce.number().int().positive().optional(),
  employmentType: z.enum(['full_time', 'part_time', 'consultant', 'intern', 'volunteer']).optional(),
  startDate: z.string().optional(),
});
type CreateFormValues = z.infer<typeof createSchema>;


/**
 * Staff ID is generated server-side, shown only after creation - never
 * a form field. The underlying user_id is likewise never typed
 * directly by HR - a real, known usability gap fixed here: HR selects
 * a real person by name from the actual user list, and the numeric
 * id is submitted automatically underneath, exactly what "automatic"
 * user id means in practice.
 */
export function StaffManagementPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [lastCreatedStaffId, setLastCreatedStaffId] = useState<string | null>(null);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const employeesQuery = useQuery({ queryKey: ['hr-employees'], queryFn: () => employeeApi.listForHR() });
  const usersQuery = useQuery({ queryKey: ['admin-users'], queryFn: () => usersApi.list(), enabled: modalOpen });
  const departmentsQuery = useQuery({ queryKey: ['admin-departments'], queryFn: () => departmentsApi.list(), enabled: modalOpen });
  const positionsQuery = useQuery({ queryKey: ['admin-positions'], queryFn: () => positionsApi.list(), enabled: modalOpen });
  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreateFormValues>({ resolver: zodResolver(createSchema) });

  const createMutation = useMutation({
    mutationFn: (values: CreateFormValues) => employeeApi.create(values),
    onSuccess: (result) => {
      showSuccess(`Staff record created - ${result.data.staffId}`);
      setLastCreatedStaffId(result.data.staffId);
      queryClient.invalidateQueries({ queryKey: ['hr-employees'] });
      reset();
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create staff record.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => employeeApi.remove(id),
    onSuccess: () => { showSuccess('Staff record deleted.'); queryClient.invalidateQueries({ queryKey: ['hr-employees'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to delete staff record.'),
  });

  const handleDelete = (id: number, fullName: string) => {
    if (window.confirm(`Delete ${fullName}'s staff record? This can only succeed if they have no contracts, leave, training, or other HR history - otherwise use "Separated" status instead.`)) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="page">
      <Link to="/hr">&larr; Back to HR Dashboard</Link>
      <div className="page-header">
        <h1>Staff Management</h1>
        <button onClick={() => setModalOpen(true)}>New Staff Record</button>
      </div>

      {lastCreatedStaffId && (
        <section className="card alert-card">
          <p>Staff record created with Staff ID <strong>{lastCreatedStaffId}</strong>.</p>
        </section>
      )}

      {employeesQuery.data && (
        <DataTable
          rows={employeesQuery.data.data}
          rowKey={(e) => e.id}
          emptyMessage="No staff records yet."
          columns={[
            { header: 'Staff ID', render: (e) => e.staff_id ?? <span className="muted">Not assigned</span> },
            { header: 'Name', render: (e) => <Link to={`/hr/staff/${e.id}`}>{e.full_name}</Link> },
            { header: 'Position', render: (e) => e.position_name ?? '-' },
            { header: 'Department', render: (e) => e.department_name ?? '-' },
            { header: 'Status', render: (e) => <StatusBadge status={e.employment_status} /> },
            {
              header: '',
              render: (e) => (
                <span className="row-actions">
                  <Link to={`/hr/staff/${e.id}`}>Edit</Link>
                  <button className="link-button" onClick={() => handleDelete(e.id, e.full_name)} disabled={deleteMutation.isPending}>Delete</button>
                </span>
              ),
            },
          ]}
        />
      )}

      {modalOpen && (
        <Modal title="New Staff Record" onClose={() => setModalOpen(false)}>
          <p className="muted">The Staff ID is generated automatically once this record is created.</p>
          <form onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
            <label>
              Person
              <select {...register('userId')}>
                <option value="">Select…</option>
                {usersQuery.data?.data.map((u) => <option key={u.id} value={u.id}>{u.full_name} ({u.email})</option>)}
              </select>
              {errors.userId && <span className="field-error">{errors.userId.message}</span>}
            </label>
            <label>
              Department
              <select {...register('departmentId')}>
                <option value="">- not set -</option>
                {departmentsQuery.data?.data.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </label>
            <label>
              Position
              <select {...register('positionId')}>
                <option value="">- not set -</option>
                {positionsQuery.data?.data.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </label>
            <label>
              Employment Type
              <select {...register('employmentType')}>
                <option value="">- not set -</option>
                <option value="full_time">Full Time</option><option value="part_time">Part Time</option>
                <option value="consultant">Consultant</option><option value="intern">Intern</option><option value="volunteer">Volunteer</option>
              </select>
            </label>
            <label>Start Date<input type="date" {...register('startDate')} /></label>
            <div className="form-actions"><button type="submit" className="btn-primary" disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating…' : 'Create'}</button><button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button></div>
          </form>
        </Modal>
      )}
    </div>
  );
}
